import * as React from 'react';
import { useIntl } from 'react-intl';
import { CmdAlert } from '@commander-services/gui-components';
import CmdBox from '@commander-services/cmd-box';
import { useQueries } from '@tanstack/react-query';
import { getEcoDriveSummaryData } from '../EcoDriveService';
import GdprService from '../../../../Services/GdprService';
import { getDecimalCommaNumber } from '../../GraphService';
import { getEcodriveData } from '../../../TripsOverviewNew/TripsOverviewService';

interface IEcoDriveData {
  current: number;
  avg7Days: number;
  avgToday: number;
  avg30Days: number;
}

interface IProps {
  vehicleId: number;
  rideId?: string;
  onlineEcoScore?: number | null;
  isGdprAlert?: boolean;
}

function Header(props: IProps) {
  const { formatMessage: f } = useIntl();
  const [ecoDriveData, setEcoDriveData] = React.useState<IEcoDriveData | null>(null);
  const [isGdprAlert, setIsGdprAlert] = React.useState(false);

  const getData = async () => {
    const response = await getEcoDriveSummaryData(props.vehicleId, props.rideId);
    if (response) {
      setEcoDriveData(response);
    }
  };

  React.useEffect(() => {
    if (props.onlineEcoScore) {
      setEcoDriveData((oldState: any) => {
        return { ...oldState, current: props.onlineEcoScore };
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.onlineEcoScore]);

  useQueries({
    queries: [
      {
        queryKey: ['ecoDriveHeaderData', props.rideId],
        queryFn: () => getData(),
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
      },
    ],
  });

  const handleGdpr = React.useCallback(async () => {
    if (props.isGdprAlert) {
      setIsGdprAlert(true);
    } else if (props.isGdprAlert === undefined && props.vehicleId && props.rideId) {
      const data = await getEcodriveData(props.vehicleId, props.rideId);
      if (data) {
        const result = GdprService.getRideGdpr(
          'showPrivateData',
          data.isPrivate,
          data.customerId,
          data.driverId
        );
        if (!result) {
          setIsGdprAlert(true);
        }
      }
    } else {
      setIsGdprAlert(false);
    }
  }, [props.isGdprAlert, props.rideId, props.vehicleId]);

  React.useEffect(() => {
    handleGdpr();
  }, [handleGdpr]);

  const getClassName = (score: number | null = ecoDriveData ? ecoDriveData.current : null) => {
    let className = 'e-widget-value e-widget-value--md disabled';
    if (score !== null) {
      if (Number(score) < 5) {
        className = 'b-eco-score-info bad';
      } else if (Number(score) < 8) {
        className = 'b-eco-score-info not-good';
      } else {
        className = 'b-eco-score-info good';
      }
    }
    return className;
  };

  const getIcon = (score: number | null = ecoDriveData ? ecoDriveData.current : null) => {
    let icon = 'e-widget-icon e-widget-icon--md cmd-icon cmd-icon-eco-drive-good disabled';
    if (score !== null) {
      if (Number(score) < 5) {
        icon = 'cmd-icon cmd-icon-eco-drive-bad';
      } else if (Number(score) < 8) {
        icon = 'cmd-icon cmd-icon-eco-drive-not-good';
      } else {
        icon = 'cmd-icon cmd-icon-eco-drive-good';
      }
    }
    return icon;
  };

  return (
    <CmdBox
      id="ecodrive-basic-information"
      title={f({ id: 'tripsOverview.section.scoreOverview' })}
    >
      {isGdprAlert && (
        <div style={{ padding: '0 0 20px 0' }}>
          <CmdAlert
            id="velocity-graph-alert"
            data={{
              title: f({ id: 'toastr.info' }),
              message: f({ id: 'graph.ecoScore.alert.message' }),
              type: 'info',
            }}
            noCancel
          />
        </div>
      )}
      <div className="row">
        <div className="col-12 col-sm-6 col-lg-3 my-3 my-lg-0">
          <div className="b-eco-score b-eco-score--active">
            <h4 className="b-eco-score-title">
              {f({ id: 'tripsOverview.section.chosenRideScore' })}
            </h4>

            <div
              className={getClassName()}
              title={
                ecoDriveData?.current === 10
                  ? f({ id: 'tripsOverview.section.maxEcoScoreValue' })
                  : f({ id: 'tripsOverview.section.infoAboutBestEcoScore' })
              }
            >
              <i className={getIcon()} />{' '}
              {getDecimalCommaNumber(ecoDriveData?.current) || <strong>-</strong>}
            </div>
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3 my-3 my-lg-0">
          <div className="b-eco-score">
            <h4 className="b-eco-score-title">
              {' '}
              {f({ id: 'tripsOverview.section.averagedScoreToday' })}
            </h4>
            {ecoDriveData?.avgToday ? (
              <div
                className={getClassName(ecoDriveData.avgToday)}
                title={
                  Number(ecoDriveData?.avgToday) === 10
                    ? f({ id: 'tripsOverview.section.maxEcoScoreValue' })
                    : f({ id: 'tripsOverview.section.infoAboutBestEcoScore' })
                }
              >
                <i className={getIcon(ecoDriveData.avgToday)} />{' '}
                {getDecimalCommaNumber(ecoDriveData.avgToday)}{' '}
                <small>
                  {ecoDriveData.current
                    ? ecoDriveData.avgToday - ecoDriveData.current < 0
                      ? `(${f({ id: 'tripsOverview.section.worseBy' })} ${getDecimalCommaNumber(
                          Math.abs(Number(ecoDriveData.avgToday) - ecoDriveData.current).toFixed(1)
                        )})`
                      : ecoDriveData.avgToday - ecoDriveData.current > 0
                        ? `(${f({ id: 'tripsOverview.section.betterBy' })} ${getDecimalCommaNumber(
                            Math.abs(Number(ecoDriveData.avgToday) - ecoDriveData.current).toFixed(
                              1
                            )
                          )})`
                        : ''
                    : ''}
                </small>
              </div>
            ) : (
              <div>-</div>
            )}
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3 my-3 my-lg-0">
          <div className="b-eco-score">
            <h4 className="b-eco-score-title">
              {f({ id: 'tripsOverview.section.averagedScore7days' })}
            </h4>
            {ecoDriveData?.avg7Days ? (
              <div
                className={getClassName(ecoDriveData.avg7Days)}
                title={
                  Number(ecoDriveData.avg7Days) === 10
                    ? f({ id: 'tripsOverview.section.maxEcoScoreValue' })
                    : f({ id: 'tripsOverview.section.infoAboutBestEcoScore' })
                }
              >
                <i className={getIcon(ecoDriveData.avg7Days)} />{' '}
                {getDecimalCommaNumber(ecoDriveData.avg7Days)}{' '}
                <small>
                  {ecoDriveData.current
                    ? ecoDriveData.avg7Days - ecoDriveData.current < 0
                      ? `(${f({ id: 'tripsOverview.section.worseBy' })} ${getDecimalCommaNumber(
                          Math.abs(Number(ecoDriveData.avg7Days) - ecoDriveData.current).toFixed(1)
                        )})`
                      : ecoDriveData.avg7Days - ecoDriveData.current > 0
                        ? `(${f({ id: 'tripsOverview.section.betterBy' })} ${getDecimalCommaNumber(
                            Math.abs(Number(ecoDriveData.avg7Days) - ecoDriveData.current).toFixed(
                              1
                            )
                          )})`
                        : ''
                    : ''}
                </small>
              </div>
            ) : (
              <div>-</div>
            )}
          </div>
        </div>
        <div className="col-12 col-sm-6 col-lg-3 my-3 my-lg-0">
          <div className="b-eco-score">
            <h4 className="b-eco-score-title">
              {f({ id: 'tripsOverview.section.averagedScore30days' })}
            </h4>
            {ecoDriveData?.avg30Days ? (
              <div
                className={getClassName(ecoDriveData.avg30Days)}
                title={
                  Number(ecoDriveData.avg30Days) === 10
                    ? f({ id: 'tripsOverview.section.maxEcoScoreValue' })
                    : f({ id: 'tripsOverview.section.infoAboutBestEcoScore' })
                }
              >
                <i className={getIcon(ecoDriveData.avg30Days)} />{' '}
                {getDecimalCommaNumber(ecoDriveData.avg30Days)}{' '}
                <small>
                  {ecoDriveData.current
                    ? ecoDriveData.avg30Days - ecoDriveData.current < 0
                      ? `(${f({ id: 'tripsOverview.section.worseBy' })} ${getDecimalCommaNumber(
                          Math.abs(Number(ecoDriveData.avg30Days) - ecoDriveData.current).toFixed(1)
                        )})`
                      : ecoDriveData.avg30Days - ecoDriveData.current > 0
                        ? `(${f({ id: 'tripsOverview.section.betterBy' })} ${getDecimalCommaNumber(
                            Math.abs(Number(ecoDriveData.avg30Days) - ecoDriveData.current).toFixed(
                              1
                            )
                          )})`
                        : ''
                    : ''}
                </small>
              </div>
            ) : (
              <div>-</div>
            )}
          </div>
        </div>
      </div>
    </CmdBox>
  );
}

export default React.memo(Header);
